// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import { addToEndOfChain } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    chain,
    DispatchCompleteSendRmaRequest,
} from "@insite/client-framework/Store/Pages/RequestRma/Handlers/SendRmaRequest";
import translate from "@insite/client-framework/Translate";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { formatCategories } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";

export const _GaSendRmaRequest: typeof DispatchCompleteSendRmaRequest = props => {
    const dataLayer = getDataLayer();
    if (!dataLayer) {
        return;
    }
    const { apiParameter } = props;
    const state = props.getState();
    const orderLines = state.pages.requestRma.orderLines;
    const orderId = state.data.orders.idByOrderNumber[apiParameter.rmaModel.orderNumber];
    const order = state.data.orders.byId[orderId];
    const session = getSession(state);
    const currentBillTo = getCurrentBillToState(state).value;
    const isAuthenticated = xIsAuthenticated(state);

    const rmaLines:typeof orderLines = [];
    let rmaLinesTotal = 0;
    orderLines?.forEach(rmaLine => {
        if (!rmaLine.rmaQtyRequested) {
            return;
        }
        const googleAnalyticsData = {
            event: "return_request",
            click_text: translate("Send Return Request"),
            destination: "",
            item_name: rmaLine.productName || rmaLine.description || "",
            item_id: rmaLine.productErpNumber,
            return_reason: rmaLine.returnReason,
            quantity: rmaLine.rmaQtyRequested,
            unit_of_measure: rmaLine.unitOfMeasure,
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: (session as any).salesperson?.name || "",
        };
        rmaLinesTotal += rmaLine.unitNetPrice * rmaLine.rmaQtyRequested;
        rmaLines.push(rmaLine);
        dataLayer.push(googleAnalyticsData);
    });

    const googleAnalyticsData = {
        event: "refund",
        coupon: order.orderPromotions,
        shipping: order.shippingAndHandling,
        tax: order.taxAmount,
        value: rmaLinesTotal,
        transaction_id: apiParameter.rmaModel.orderNumber,
        currency: session.currency?.currencyCode,
        authentication_state: isAuthenticated ? "Authenticated" : "Guest",
        branch_number: (session as any)?.defaultWarehouse?.name || "",
        branch_name: (session as any)?.defaultWarehouse?.description || "",
        sales_rep: (session as any).salesperson?.name || "",
        affiliation: currentBillTo?.companyName || "",

        items: rmaLines.map((rmaLine, index) => ({
            item_id: rmaLine.productErpNumber,
            item_name: rmaLine.productName || rmaLine.description || "",
            price: rmaLine.unitNetPrice,
            quantity: rmaLine.rmaQtyRequested,
            affiliation: currentBillTo?.companyName || "",
            item_list_id: "",
            item_list_name: "",
            item_brand: rmaLine.brand?.name || "",
            ...formatCategories(rmaLine.properties?.category),
            discount: rmaLine.unitNetPrice < rmaLine.unitListPrice ? rmaLine.unitListPrice - rmaLine.unitNetPrice : 0,
            index,
            item_variant: rmaLine.manufacturerItem || "",
            location_id: (session as any)?.pickUpWarehouse?.name || "",
        })),
    };
    dataLayer.push(googleAnalyticsData);
};

addToEndOfChain(chain, _GaSendRmaRequest);
